<div class="biz-docs-section" id="tour-business-documents">
  <!-- Section Header -->
  <div class="profiles-header">
    <h3>Business Documents</h3>
    @if (canUploadMore()) {
      <!-- Hidden file input — triggered directly by the Upload button -->
      <input
        #fileInput
        type="file"
        class="biz-docs-file-input"
        [accept]="acceptAttr"
        multiple
        (change)="onFileInputChange($event)"
        aria-hidden="true"
        tabindex="-1"
      />
      <button
        class="edit-info-btn"
        type="button"
        (click)="fileInput.click()"
        aria-label="Upload documents"
      >
        <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path
            d="M4 16v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2M12 12V4M8 8l4-4 4 4"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
        Upload
      </button>
    }
  </div>

  <!-- Global error banner (only when modal is closed) -->
  @if (globalError() && !showConsentModal()) {
    <div class="biz-docs-error-banner" role="alert">
      <svg
        viewBox="0 0 24 24"
        fill="currentColor"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"
        />
      </svg>
      <span>{{ globalError() }}</span>
      <button
        class="biz-docs-error-dismiss"
        type="button"
        (click)="dismissError()"
        aria-label="Dismiss"
      >
        <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path
            d="M18 6L6 18M6 6l12 12"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
      </button>
    </div>
  }

  <!-- File Cards Grid -->
  @if (isLoadingFiles()) {
    <div class="biz-docs-skeleton-grid">
      <div class="biz-docs-skeleton"></div>
      <div class="biz-docs-skeleton"></div>
    </div>
  } @else if (existingFiles().length > 0) {
    <div class="biz-docs-grid">
      @for (doc of existingFiles(); track doc.id) {
        <div
          class="biz-doc-card"
          [class.confirm-mode]="pendingDeleteId() === doc.id"
          [class.clickable]="pendingDeleteId() !== doc.id"
          (click)="viewFile(doc, $event)"
          (keydown.enter)="viewFile(doc, $event)"
          (keydown.space)="viewFile(doc, $event); $event.preventDefault()"
          role="button"
          [attr.tabindex]="pendingDeleteId() !== doc.id ? 0 : -1"
          [attr.aria-label]="
            'View document ' + (doc.file_name || doc.blob_name)
          "
        >
          <!-- File type icon -->
          <div
            class="biz-doc-card-icon"
            [attr.data-type]="getFileIconType(doc.file_name || doc.blob_name)"
          >
            <ng-container
              *ngTemplateOutlet="
                fileIconTpl;
                context: {
                  type: getFileIconType(doc.file_name || doc.blob_name),
                }
              "
            ></ng-container>
          </div>
          <!-- File info -->
          <div class="biz-doc-card-content">
            <h4
              class="biz-doc-card-name"
              [title]="doc.file_name || doc.blob_name"
            >
              {{ doc.file_name || doc.blob_name }}
            </h4>
            <p class="biz-doc-card-meta">
              @if (doc.file_size) {
                {{ formatFileSize(doc.file_size) }} ·
              }
              {{ formatDate(doc.uploaded_datetime) }}
            </p>
            @if (
              doc.upload_status &&
              doc.upload_status !== FileUploadStatus.Success
            ) {
              <div
                class="biz-doc-status-tag"
                [ngClass]="getUploadStatusTagClass(doc.upload_status)"
              >
                @if (doc.upload_status === FileUploadStatus.Pending) {
                  <span class="status-dot animated"></span>
                }
                {{ getUploadStatusText(doc.upload_status) }}
              </div>
            }
          </div>
          <!-- Delete / Confirm -->
          @if (pendingDeleteId() === doc.id) {
            <div class="biz-doc-confirm-row">
              <span>Remove?</span>
              <button
                class="biz-doc-confirm-yes"
                type="button"
                (click)="deleteDocument(doc.id)"
              >
                Yes
              </button>
              <button
                class="biz-doc-confirm-no"
                type="button"
                (click)="cancelDelete()"
              >
                No
              </button>
            </div>
          } @else {
            <div class="biz-doc-actions-row">
              <button
                class="biz-doc-delete-btn"
                type="button"
                (click)="confirmDelete(doc.id)"
                aria-label="Remove document"
                title="Remove document"
              >
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="1.8"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <polyline points="3 6 5 6 21 6"></polyline>
                  <path
                    d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"
                  ></path>
                </svg>
              </button>
            </div>
          }
        </div>
      }
    </div>
  } @else if (!isLoadingFiles()) {
    <!-- Empty state -->
    <div class="biz-docs-empty">
      <svg
        viewBox="0 0 24 24"
        fill="currentColor"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6zm-1 1.5L18.5 9H13V3.5zM6 20V4h5v7h7v9H6z"
        />
      </svg>
      <p class="biz-docs-empty-title">No documents uploaded yet</p>
      <p class="biz-docs-empty-desc">
        Click <strong>Upload</strong> to attach supporting documents to your
        profile.
      </p>
    </div>
  }
</div>

<!-- ── Consent Modal ─────────────────────────────────────────────────────────── -->
@if (showConsentModal()) {
  <div
    class="biz-docs-modal-backdrop"
    (click)="closeConsentModal()"
    (keydown.escape)="closeConsentModal()"
    tabindex="-1"
    aria-hidden="true"
  ></div>

  <div
    class="biz-docs-modal"
    role="dialog"
    aria-modal="true"
    aria-labelledby="consent-modal-title"
  >
    <!-- Modal Header -->
    <div class="biz-docs-modal-header">
      <div class="biz-docs-modal-title-row">
        <div class="biz-docs-modal-icon">
          <svg
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M4 16v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2M12 12V4M8 8l4-4 4 4"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </div>
        <div>
          <h3 id="consent-modal-title" class="biz-docs-modal-title">
            Upload Documents
          </h3>
        </div>
      </div>
      <button
        class="biz-docs-modal-close"
        type="button"
        (click)="closeConsentModal()"
        aria-label="Cancel and close"
        [disabled]="isUploading()"
      >
        <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path
            d="M18 6L6 18M6 6l12 12"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
      </button>
    </div>

    <!-- Selected Files List -->
    <div class="biz-docs-modal-files-section">
      <!-- Files section header with Add more button -->
      <div class="biz-docs-modal-files-header">
        <span class="biz-docs-modal-files-label">
          {{ pendingFiles().length }}
          {{ pendingFiles().length === 1 ? "file" : "files" }} selected
        </span>
        @if (!isUploading() && pendingFiles().length + usedSlots() < maxFiles) {
          <!-- hidden input for adding more files inside the modal -->
          <input
            #modalFileInput
            type="file"
            class="biz-docs-file-input"
            [accept]="acceptAttr"
            multiple
            (change)="onModalFileInputChange($event)"
            aria-hidden="true"
            tabindex="-1"
          />
          <button
            class="biz-docs-add-more-btn"
            type="button"
            (click)="modalFileInput.click()"
            [disabled]="isUploading()"
            title="Add more files"
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M12 5v14M5 12h14"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
              />
            </svg>
            Add files
          </button>
        }
      </div>

      <!-- File rows -->
      <div class="biz-docs-modal-files">
        @for (file of pendingFiles(); track file.name; let i = $index) {
          <div class="biz-docs-modal-file-row">
            <div
              class="biz-doc-card-icon biz-doc-card-icon--sm"
              [attr.data-type]="getFileIconType(file.name)"
            >
              <ng-container
                *ngTemplateOutlet="
                  fileIconTpl;
                  context: { type: getFileIconType(file.name) }
                "
              ></ng-container>
            </div>
            <div class="biz-docs-modal-file-info">
              <span class="biz-docs-modal-file-name" [title]="file.name">{{
                file.name
              }}</span>
              <span class="biz-docs-modal-file-size">{{
                formatFileSizeFromFile(file)
              }}</span>
            </div>
            <!-- Remove button -->
            @if (!isUploading()) {
              <button
                class="biz-docs-modal-file-remove"
                type="button"
                (click)="removePendingFile(i)"
                [attr.aria-label]="'Remove ' + file.name"
                title="Remove file"
              >
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M18 6L6 18M6 6l12 12"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
              </button>
            }
          </div>
        }
      </div>
    </div>

    <!-- Divider -->
    <div class="biz-docs-modal-divider"></div>

    <!-- Consent Checkboxes -->
    <div class="biz-docs-consent-checkboxes">
      <div class="biz-docs-consent-item">
        <span class="biz-docs-consent-required">*</span>
        <label class="biz-docs-consent-label">
          <input
            type="checkbox"
            class="biz-docs-consent-check"
            [checked]="hasConsentChecked()"
            (change)="hasConsentChecked.set($any($event.target).checked)"
            [disabled]="isUploading()"
          />
          <span>
            By uploading this document, you verify that you have the proper
            rights and permissions to upload, share, or view this document. The
            information in this file is up to date, accurate, and does not
            contain any illegal or malicious contents.
          </span>
        </label>
      </div>

      <div class="biz-docs-consent-item">
        <span class="biz-docs-consent-required">*</span>
        <label class="biz-docs-consent-label">
          <input
            type="checkbox"
            class="biz-docs-consent-check"
            [checked]="hasTermsChecked()"
            (change)="hasTermsChecked.set($any($event.target).checked)"
            [disabled]="isUploading()"
          />
          <span>
            I agree to the ACC Data Hub
            <a
              href="/terms-of-use"
              target="_blank"
              (click)="$event.stopPropagation()"
              >Terms of Use</a
            >.
          </span>
        </label>
      </div>
    </div>

    <!-- Modal Error (shown inside modal when globalError is set) -->
    @if (globalError()) {
      <div class="biz-docs-modal-error" role="alert">
        <svg
          viewBox="0 0 24 24"
          fill="currentColor"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"
          />
        </svg>
        <span>{{ globalError() }}</span>
        <button
          class="biz-docs-modal-error-dismiss"
          type="button"
          (click)="dismissError()"
          aria-label="Dismiss"
        >
          <svg
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M18 6L6 18M6 6l12 12"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </button>
      </div>
    }

    <!-- Modal Footer -->
    <div class="biz-docs-modal-footer">
      <button
        class="biz-docs-modal-btn-cancel"
        type="button"
        (click)="closeConsentModal()"
        [disabled]="isUploading()"
      >
        Cancel
      </button>
      <button
        class="biz-docs-modal-btn-upload"
        type="button"
        (click)="submitUpload()"
        [disabled]="!canProceedWithUpload() || isUploading()"
      >
        @if (isUploading()) {
          <span class="biz-docs-modal-spinner"></span>
          Uploading…
        } @else {
          <svg
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M4 16v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2M12 12V4M8 8l4-4 4 4"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
          Upload
        }
      </button>
    </div>
  </div>
}

<!-- Reusable file icon template -->
<ng-template #fileIconTpl let-type="type">
  @switch (type) {
    @case ("pdf") {
      <svg
        viewBox="0 0 24 24"
        fill="currentColor"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M20 2H8c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm-8.5 7.5c0 .83-.67 1.5-1.5 1.5H9v2H7.5V7H10c.83 0 1.5.67 1.5 1.5v1zm5 2c0 .83-.67 1.5-1.5 1.5h-2.5V7H15c.83 0 1.5.67 1.5 1.5v3zm4-3H19v1h1.5V11H19v2h-1.5V7H20.5v1.5zM9 9.5h1v-1H9v1zM4 6H2v14c0 1.1.9 2 2 2h14v-2H4V6zm10 5.5h1v-3h-1v3z"
        />
      </svg>
    }
    @case ("word") {
      <svg
        viewBox="0 0 24 24"
        fill="currentColor"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6zm-1 1.5L18.5 9H13V3.5zM8 16h2l1-4 1 4h2l1.5-6h-1.5l-.75 3.5L13 10h-2l-.75 3.5L9.5 10H8L9.5 16H8z"
        />
      </svg>
    }
    @case ("excel") {
      <svg
        viewBox="0 0 24 24"
        fill="currentColor"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6zm-1 1.5L18.5 9H13V3.5zM9 17l-2-3 2-3h1.5l-2 3 2 3H9zm5 0h-1.5l-2-3 2-3H14l-2 3 2 3z"
        />
      </svg>
    }
    @case ("image") {
      <svg
        viewBox="0 0 24 24"
        fill="currentColor"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z"
        />
      </svg>
    }
    @default {
      <svg
        viewBox="0 0 24 24"
        fill="currentColor"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6zm-1 1.5L18.5 9H13V3.5zM6 20V4h5v7h7v9H6z"
        />
      </svg>
    }
  }
</ng-template>

<app-file-viewer
  [fileUrl]="viewerFileUrl"
  [fileName]="viewerFileName"
  [showViewer]="showFileViewer"
  (closeViewer)="onCloseViewer()"
  (downloadFile)="onViewerDownload()"
></app-file-viewer>
